/**
 * Primes: a grid of numbers you knock over.
 *
 * Tap 2 and every multiple of 2 falls — but not 2 itself. Then 3, then 5. What
 * is left standing at the end are the primes, and nobody had to define one.
 * The sieve of Eratosthenes, played rather than explained.
 */

import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, RotateCcw, Wand2 } from 'lucide-preact';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { isTap, useSurface } from '../../hooks/useSurface.ts';
import type { SieveState } from '../../lib/sieve.ts';
import { createSieve, isComplete, isStanding, multiplesOf, nextToStrike, strike } from '../../lib/sieve.ts';
import { Chip, Coach, ToyButton } from '../../ui/controls.tsx';

const LIMITS = [50, 100, 200, 400];
/** The readout pill floats over the canvas; the grid starts below it. */
const TOP = 152;
/** Kept clear at the bottom for the coach line. */
const BOTTOM = 76;

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

interface Layout {
  cols: number;
  cell: number;
  originX: number;
  originY: number;
}

export function PrimeGridToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const layoutRef = useRef<Layout>({ cols: 10, cell: 30, originX: 0, originY: 0 });
  const scrollRef = useRef(0);
  const maxScrollRef = useRef(0);
  /** Numbers knocked over this instant, so they can fall rather than vanish. */
  const fallingRef = useRef(new Map<number, number>());

  const [limit, setLimit] = useState(100);
  const [sieve, setSieve] = useState<SieveState>(() => createSieve(100));
  const live = useRef(sieve);
  live.current = sieve;

  const standing = countStanding(sieve);
  const done = isComplete(sieve);

  const knock = (p: number) => {
    if (!isStanding(live.current, p) || live.current.used.includes(p)) return;
    const falling = fallingRef.current;
    for (const n of multiplesOf(p, live.current.limit)) {
      if (!live.current.struck.has(n)) falling.set(n, 0);
    }
    setSieve(strike(live.current, p));
    play('whoosh', 0.55);
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      spawnSparks(sparksRef.current, { x: rect.width / 2, y: rect.height * 0.4 }, partyColors(), 16, 300);
    }
  };

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      updateSparks(sparksRef.current, dt);
      const state = live.current;
      const falling = fallingRef.current;
      for (const [n, t] of falling) {
        if (t > 1) falling.delete(n);
        else falling.set(n, t + dt * 1.8);
      }

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      const padX = 14;
      const cols = Math.max(5, Math.round(Math.sqrt(state.limit * 1.6)));
      const cell = Math.min((width - padX * 2) / cols, 52);
      const rows = Math.ceil(state.limit / cols);
      const gridH = rows * cell;
      const viewH = height - TOP - BOTTOM;
      maxScrollRef.current = Math.max(0, gridH - viewH);
      scrollRef.current = Math.max(0, Math.min(maxScrollRef.current, scrollRef.current));

      const originX = width / 2 - (cols * cell) / 2;
      // Centre a grid that fits; scroll one that does not.
      const originY = TOP + Math.max(0, (viewH - gridH) / 2) - scrollRef.current;
      layoutRef.current = { cols, cell, originX, originY };

      ctx.save();
      ctx.beginPath();
      ctx.rect(0, TOP - 6, width, viewH + 12);
      ctx.clip();

      for (let n = 1; n <= state.limit; n++) {
        const col = (n - 1) % cols;
        const row = Math.floor((n - 1) / cols);
        const fall = falling.get(n) ?? 0;
        const x = originX + col * cell;
        const y = originY + row * cell + fall * fall * 90;
        if (y < TOP - cell * 2 || y > height + cell) continue;
        drawNumber(ctx, n, x, y, cell, state, fall);
      }
      ctx.restore();

      drawSparks(ctx, sparksRef.current);
    },

    onMove: (p) => {
      // Dragging scrolls the grid when it is taller than the paper.
      if (maxScrollRef.current <= 0) return;
      const dy = p.pos.y - p.start.y;
      if (Math.abs(dy) < 4) return;
      scrollRef.current = Math.max(0, Math.min(maxScrollRef.current, scrollRef.current - dy * 0.35));
    },

    onUp: (p) => {
      if (!isTap(p)) return;
      const { cols, cell, originX, originY } = layoutRef.current;
      const col = Math.floor((p.pos.x - originX) / cell);
      const row = Math.floor((p.pos.y - originY) / cell);
      if (col < 0 || col >= cols || row < 0) return;
      const n = row * cols + col + 1;
      if (n < 2 || n > live.current.limit) return;
      knock(n);
    },
  });

  const restart = (nextLimit = limit) => {
    setLimit(nextLimit);
    setSieve(createSieve(nextLimit));
    fallingRef.current.clear();
    scrollRef.current = 0;
    play('whoosh', 0.6);
  };

  const next = nextToStrike(sieve);

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Prime number grid" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton
              round
              label="Knock over the next one"
              hideLabel
              disabled={next === null || done}
              onClick={() => next !== null && knock(next)}
            >
              <Wand2 size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Stand them all back up" hideLabel onClick={() => restart()}>
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>{standing} standing</strong>
          <span>{done ? 'all of them are primes' : `tap ${next ?? '2'} to knock over its multiples`}</span>
        </p>

        {done && <Coach>Nothing knocks these over. They are the primes</Coach>}
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row dock__row--scroll" role="group" aria-label="How far to count">
          {LIMITS.map((n) => (
            <Chip key={n} label={`up to ${n}`} pressed={limit === n} onClick={() => restart(n)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function countStanding(state: SieveState): number {
  let count = 0;
  for (let n = 2; n <= state.limit; n++) {
    if (isStanding(state, n)) count++;
  }
  return count;
}

function drawNumber(
  ctx: CanvasRenderingContext2D,
  n: number,
  x: number,
  y: number,
  cell: number,
  state: SieveState,
  fall: number
): void {
  const knocked = state.struck.has(n);
  const used = state.used.includes(n);
  const one = n === 1;

  ctx.save();
  if (fall > 0) {
    ctx.globalAlpha = Math.max(0, 1 - fall);
    ctx.translate(x + cell / 2, y + cell / 2);
    ctx.rotate(fall * 1.6);
    ctx.translate(-cell / 2, -cell / 2);
    x = 0;
    y = 0;
  } else if (knocked || one) {
    ctx.globalAlpha = 0.28;
  }

  const pad = cell * 0.08;
  ctx.fillStyle = used ? token('--crayon-green') : knocked || one ? token('--ink-faint') : token('--crayon-yellow');
  ctx.strokeStyle = 'rgba(44, 37, 69, 0.7)';
  ctx.lineWidth = knocked ? 1 : 2;
  ctx.beginPath();
  ctx.roundRect(x + pad, y + pad, cell - pad * 2, cell - pad * 2, cell * 0.24);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = used ? '#ffffff' : token('--ink');
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${Math.min(cell * 0.42, 20)}px ui-rounded, system-ui, sans-serif`;
  ctx.fillText(String(n), x + cell / 2, y + cell / 2);
  ctx.restore();
}
